import React, { useState, useEffect } from 'react';
import { Event } from '../types';
import { adminApi } from '../services/api';
import EventForm from './EventForm';

interface AdminDashboardProps {
  onLogout: () => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ onLogout }) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | undefined>();

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const eventsData = await adminApi.getAllEvents();
      setEvents(eventsData);
    } catch (error) {
      console.error('Failed to load events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (data: any) => {
    await adminApi.createEvent(data);
    setShowForm(false);
    loadEvents();
  };

  const handleUpdateEvent = async (data: any) => {
    if (editingEvent) {
      await adminApi.updateEvent(editingEvent.id, data);
      setEditingEvent(undefined);
      setShowForm(false);
      loadEvents();
    }
  };

  const handleDeleteEvent = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      await adminApi.deleteEvent(id);
      loadEvents();
    }
  };

  const handleRemoveGuest = async (eventId: string, guestId: string) => {
    if (window.confirm('Are you sure you want to remove this guest?')) {
      await adminApi.removeGuest(eventId, guestId);
      loadEvents();
    }
  };

  const copyInviteLink = (eventId: string) => {
    const link = `${window.location.origin}/event/${eventId}/invite`;
    navigator.clipboard.writeText(link);
    alert('Invite link copied to clipboard!');
  };

  const copyEventLink = (eventId: string) => {
    const link = `${window.location.origin}/event/${eventId}`;
    navigator.clipboard.writeText(link);
    alert('Event link copied to clipboard!');
  };

  if (showForm) {
    return (
      <EventForm
        event={editingEvent}
        onSubmit={editingEvent ? handleUpdateEvent : handleCreateEvent}
        onCancel={() => {
          setShowForm(false);
          setEditingEvent(undefined);
        }}
      />
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Admin Dashboard</h1>
        <div>
          <button
            onClick={() => setShowForm(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              marginRight: '10px',
              cursor: 'pointer'
            }}
          >
            Create New Event
          </button>
          <button
            onClick={onLogout}
            style={{
              padding: '10px 20px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {loading ? (
        <div>Loading events...</div>
      ) : events.length === 0 ? (
        <div>No events yet. Create your first event!</div>
      ) : (
        <div style={{ display: 'grid', gap: '20px' }}>
          {events.map(event => (
            <div key={event.id} style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <h3>{event.title}</h3>
                  <p><strong>Location:</strong> {event.location}</p>
                  {event.description && <p><strong>Description:</strong> {event.description}</p>}
                  {event.instructions && <p><strong>Instructions:</strong> {event.instructions}</p>}

                  <div style={{ marginTop: '15px' }}>
                    <h4>Guests ({event.guests.length})</h4>
                    {event.guests.length === 0 ? (
                      <p>No guests registered yet.</p>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
                        {event.guests.map(guest => (
                          <div
                            key={guest.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '5px 10px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '20px',
                              border: '1px solid #dee2e6'
                            }}
                          >
                            <div
                              style={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '50%',
                                backgroundColor: '#007bff',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: '8px',
                                fontSize: '14px',
                                fontWeight: 'bold'
                              }}
                            >
                              {guest.profileLetter}
                            </div>
                            <span style={{ marginRight: '8px' }}>{guest.name}</span>
                            <button
                              onClick={() => handleRemoveGuest(event.id, guest.id)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#dc3545',
                                cursor: 'pointer',
                                fontSize: '16px'
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginLeft: '20px' }}>
                  <button
                    onClick={() => {
                      setEditingEvent(event);
                      setShowForm(true);
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteEvent(event.id)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => copyEventLink(event.id)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#17a2b8',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Copy Event Link
                  </button>
                  <button
                    onClick={() => copyInviteLink(event.id)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#ffc107',
                      color: 'black',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Copy Invite Link
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;